/**
 * villageStoryMapping.js — the ONE place `GET /api/village/story` and `GET /api/village/presence`
 * become Village props (#583/#584 N1 slice S8; design D1, D2, D9).
 *
 * The story payload is S5's `villageStory.service.js`. Three rules shape this file:
 *
 * - **A milestone is an award the server recorded, never a percentage.** Nothing here looks at a
 *   project's total to decide what has been built (D2: "Rejected: recomputing buildings from the
 *   current percentage, because a new project erases the story"). A project at 100% with no award
 *   row shows no decoration until the award worker writes one, and `awardsPending` says so.
 * - **People are listed in name order, everyone, with no amount.** Contributors and houses are
 *   sorted here by name even though the server already sorts, so a changed server order can never
 *   turn the list into a ranking, and only `userId`, `name` and `current` survive the mapping (D9).
 * - **Every scene target has an id the DOM shares.** `sceneTargetId` is the single naming rule, so
 *   S9's scene and this file's list buttons open the same detail.
 *
 * Presence is a separate read with its own timestamp (the endpoint table's `GET /api/village/story`
 * row). It is mapped here only so its rows and its one arrival summary have one home.
 */

const MILESTONE_LABELS = {
  quarter: 'A quarter of the way',
  half: 'Half way',
  'three-quarters': 'Three quarters of the way',
  complete: 'Project complete',
};

/** S5's two correction notes (`village_milestone_guard`), said as what happened. */
const CORRECTION_TEXT = {
  total_below_threshold: 'Record corrected: the recorded total fell below this mark. The milestone stays.',
  source_corrected: 'Record corrected: the source behind this milestone was corrected. The milestone stays.',
};
const CORRECTION_FALLBACK = 'Record corrected. The milestone stays.';

const NAMELESS = 'A family member';

export function sceneTargetId(kind, ...parts) {
  return [kind, ...parts].join(':');
}

function nameOf(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : NAMELESS;
}

function projectTitle(value) {
  return (typeof value === 'string' && value.trim()) || 'Your family’s village project';
}

function number(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Name order, then id, so two people with one name still have a fixed order. Never a metric. */
function byName(a, b) {
  return a.name.localeCompare(b.name) || String(a.userId).localeCompare(String(b.userId));
}

// Validate the facts we render before mapping. Missing evidence is not a real zero or empty list.
const record = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const id = (v) => (typeof v === 'string' && v.trim() !== '') || (Number.isInteger(v) && v > 0);
const amount = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0;
const instant = (v) => typeof v === 'string' && Number.isFinite(Date.parse(v));
const optionalInstant = (v) => v == null || instant(v);
const person = (v) => record(v) && id(v.userId);
const list = (v, valid) => Array.isArray(v) && v.every(valid);
function validMilestone(m) {
  return record(m) && typeof m.key === 'string' && amount(m.threshold) && instant(m.earnedAt)
    && optionalInstant(m.recordedAt)
    && (m.sourceType !== 'imported' || instant(m.recordedAt))
    && (m.correction == null || (record(m.correction) && instant(m.correction.at)));
}
function validProject(p) {
  return record(p) && id(p.projectId) && amount(p.total) && amount(p.pointTarget)
    && instant(p.startedAt) && optionalInstant(p.endedAt) && optionalInstant(p.historyBeganAt)
    && list(p.milestones, validMilestone) && list(p.contributors, person);
}

function toMilestone(m, projectId, projectTitle) {
  const correction = m.correction && m.correction.at
    ? { at: m.correction.at, note: m.correction.note, text: CORRECTION_TEXT[m.correction.note] || CORRECTION_FALLBACK }
    : null;
  return {
    id: sceneTargetId('milestone', projectId, m.key),
    kind: 'milestone',
    key: m.key,
    label: MILESTONE_LABELS[m.key] || 'A milestone',
    threshold: number(m.threshold),
    earnedAt: m.earnedAt || null,
    // S5 writes `recorded_at` only on an award made when the surviving project was imported.
    recordedAtHistoryStart: m.sourceType === 'imported' && !!m.recordedAt,
    correction,
    projectTitle,
  };
}

function toProject(p) {
  if (!p || typeof p !== 'object' || p.projectId == null) return null;
  const projectId = String(p.projectId);
  const title = projectTitle(p.title);
  return {
    projectId,
    title,
    total: number(p.total),
    pointTarget: number(p.pointTarget),
    startedAt: p.startedAt || null,
    endedAt: p.endedAt || null,
    historyBeganAt: p.historyBeganAt || null,
    milestones: (Array.isArray(p.milestones) ? p.milestones : []).map((m) => toMilestone(m, projectId, title)),
    contributors: (Array.isArray(p.contributors) ? p.contributors : [])
      .map((c) => ({ userId: c.userId, name: nameOf(c.firstName), current: c.current !== false }))
      .sort(byName),
  };
}

/**
 * The story, `{ enabled: false }`, or `null` for a payload this screen cannot read.
 * @returns {null | {enabled: false} | object}
 */
export function toVillageStory(payload) {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.enabled === false) return { enabled: false };
  if (payload.enabled !== true || !list(payload.houses, person) || !list(payload.earlier, validProject)
      || (payload.current !== null && !validProject(payload.current)) || !instant(payload.asOf)
      || !optionalInstant(payload.awardsPendingSince)) return null;

  const current = toProject(payload.current);
  const earlier = payload.earlier.map(toProject).filter(Boolean);
  const houses = payload.houses
    .map((h) => ({ id: sceneTargetId('house', h.userId), kind: 'house', userId: h.userId, name: nameOf(h.firstName) }))
    .sort(byName);
  const u = payload.unrecordedProject;

  return {
    enabled: true,
    asOf: payload.asOf || null,
    awardsPending: !!payload.awardsPendingSince,
    unrecorded: u && typeof u === 'object' ? { title: projectTitle(u.title), pointTarget: number(u.pointTarget) } : null,
    current,
    earlier,
    moreEarlier: payload.moreEarlier === true,
    houses,
    // Every target a scene may show, in the order `VillageStory` renders them: the current project's
    // milestones, then the houses, then the earlier projects' milestones (review r1 M2).
    targets: [...(current ? current.milestones : []), ...houses, ...earlier.flatMap((p) => p.milestones)],
  };
}

/** The Village project card from the story's current project: the same override rules as `villageMapping.js`. */
export function toStoryProjectCard(current) {
  if (!current) return null;
  const card = {
    eyebrow: 'Village project',
    title: current.title,
    unit: 'points',
    value: current.total,
    reward: null,
    rewardName: '',
    rewardOutcome: '',
    // The story lists contributors itself, by name and with no amount; the card's pills carry counts.
    contributors: [],
  };
  if (current.pointTarget > 0) card.goal = current.pointTarget;
  return card;
}

const UNNAMED_DISPLAY = 'A shared display';

/**
 * `GET /api/village/presence` → PresenceFeed rows stamped with the read's own time.
 *
 * The household's shared displays are kept, as rows of their own kind (review r1 L3): dropping them
 * let the panel say "Nobody in your family has ChoreStory open" with the kitchen display on. A
 * display is not a person, so its row says what it is and `arrivalSummary` never announces it.
 */
export function toPresence(payload) {
  const meta = payload && payload.meta ? payload.meta : {};
  const reachable = meta.reachable === true && instant(meta.timestamp)
    && list(payload.members, person) && list(payload.displays, (d) => record(d) && id(d.deviceId));
  const asOf = meta.timestamp || null;
  const listOf = (key) => (reachable && payload && Array.isArray(payload[key]) ? payload[key] : []);
  const members = listOf('members').map((m) => ({
    id: m.userId,
    kind: 'member',
    actor: nameOf(m.firstName),
    name: nameOf(m.firstName),
    action: 'has ChoreStory open',
    timestamp: asOf,
    avatar: m.avatarUrl || undefined,
  }));
  const displays = listOf('displays').map((d) => {
    const label = (typeof d.name === 'string' && d.name.trim()) || UNNAMED_DISPLAY;
    return {
      id: sceneTargetId('display', d.deviceId),
      kind: 'display',
      actor: label,
      name: label,
      action: 'is showing the family board',
      timestamp: asOf,
    };
  });
  return { reachable, asOf, scope: meta.scope || null, rows: [...members, ...displays] };
}

/**
 * ONE polite sentence for everyone who arrived since the last read, never one per person (D1:
 * "Do not announce every glint. One polite summary can report several newly confirmed updates").
 * The first read announces nothing: it is the page arriving, not a person.
 */
export function arrivalSummary(previousIds, rows) {
  if (!previousIds) return '';
  // People only: a display switching on is not someone coming into the village.
  const arrived = rows.filter((r) => r.kind !== 'display' && !previousIds.has(r.id)).map((r) => r.actor);
  if (arrived.length === 0) return '';
  if (arrived.length === 1) return `${arrived[0]} came into the village.`;
  const list = `${arrived.slice(0, -1).join(', ')} and ${arrived[arrived.length - 1]}`;
  return `${arrived.length} people came into the village: ${list}.`;
}
